package my.drivebit.viewmodels

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import my.drivebit.network.NetworkException
import my.drivebit.network.services.Booking
import my.drivebit.network.services.BookingCheckoutKind
import my.drivebit.network.services.BookingDTO
import my.drivebit.network.services.PayBookingResult
import my.drivebit.network.services.Payment
import my.drivebit.network.services.checkoutBooking
import my.drivebit.utils.safeLaunchWithErrorHandler

interface MyBookingsAsRenterViewModel {
    val bookings: StateFlow<List<BookingDTO>>
    val isLoading: StateFlow<Boolean>
    val error: StateFlow<String?>
    val isPaying: StateFlow<Boolean>
    val actionInProgress: StateFlow<Set<String>>
    val payEffects: SharedFlow<ChatPayEffect>
    val signEffects: SharedFlow<ContractSignEffect>

    fun loadBookings()

    fun refreshBookings()

    fun payBooking(
        bookingId: String,
        returnUrl: String,
        failUrl: String,
    )

    fun prepayBooking(
        bookingId: String,
        returnUrl: String,
        failUrl: String,
    )

    fun signContractAsRenter(bookingId: String)
}

class MyBookingsAsRenterViewModelImpl(
    private val booking: Booking,
    private val payment: Payment,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default),
) : MyBookingsAsRenterViewModel {
    private val _bookings = MutableStateFlow<List<BookingDTO>>(emptyList())
    override val bookings: StateFlow<List<BookingDTO>> = _bookings.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    override val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _error = MutableStateFlow<String?>(null)
    override val error: StateFlow<String?> = _error.asStateFlow()

    private val _isPaying = MutableStateFlow(false)
    override val isPaying: StateFlow<Boolean> = _isPaying.asStateFlow()

    private val _actionInProgress = MutableStateFlow<Set<String>>(emptySet())
    override val actionInProgress: StateFlow<Set<String>> = _actionInProgress.asStateFlow()

    private val _payEffects = MutableSharedFlow<ChatPayEffect>(extraBufferCapacity = 1)
    override val payEffects: SharedFlow<ChatPayEffect> = _payEffects.asSharedFlow()

    private val _signEffects = MutableSharedFlow<ContractSignEffect>(extraBufferCapacity = 1)
    override val signEffects: SharedFlow<ContractSignEffect> = _signEffects.asSharedFlow()

    override fun loadBookings() {
        if (_isLoading.value) return

        coroutineScope.safeLaunchWithErrorHandler(
            isLoading = { _isLoading.value },
            setLoading = { _isLoading.value = it },
            setError = { _error.value = it },
            errorHandler = { e ->
                ErrorHandler.extractErrorMessage(
                    exception = e,
                    defaultNetworkError = "Ошибка сети",
                    defaultGenericError = "Не удалось загрузить бронирования",
                )
            },
        ) {
            val result = booking.getMyAsRenter()
            _bookings.value = result
        }
    }

    override fun refreshBookings() {
        coroutineScope.launch {
            runCatching {
                val result = booking.getMyAsRenter()
                _bookings.value = result
                _error.value = null
            }
        }
    }

    override fun payBooking(
        bookingId: String,
        returnUrl: String,
        failUrl: String,
    ) {
        checkoutBooking(
            bookingId = bookingId,
            kind = BookingCheckoutKind.FullOrBalance,
            returnUrl = returnUrl,
            failUrl = failUrl,
        )
    }

    override fun prepayBooking(
        bookingId: String,
        returnUrl: String,
        failUrl: String,
    ) {
        checkoutBooking(
            bookingId = bookingId,
            kind = BookingCheckoutKind.Prepayment,
            returnUrl = returnUrl,
            failUrl = failUrl,
        )
    }

    private fun checkoutBooking(
        bookingId: String,
        kind: BookingCheckoutKind,
        returnUrl: String,
        failUrl: String,
    ) {
        if (bookingId.isBlank() || _isPaying.value) return
        coroutineScope.launch {
            _isPaying.value = true
            try {
                when (val result = payment.checkoutBooking(bookingId, kind, returnUrl, failUrl)) {
                    is PayBookingResult.Redirect ->
                        _payEffects.emit(ChatPayEffect.OpenCheckout(result.url, bookingId = bookingId))
                    is PayBookingResult.AlreadyPaid -> {
                        val text =
                            result.message?.takeIf { it.isNotBlank() }
                                ?: "Оплата уже выполнена"
                        _payEffects.emit(
                            ChatPayEffect.ShowInfo(
                                text = text,
                                bookingId = bookingId,
                                alreadyPaid = true,
                            ),
                        )
                        loadBookings()
                    }
                    is PayBookingResult.Failed ->
                        _payEffects.emit(
                            ChatPayEffect.ShowInfo(
                                text = result.message,
                                bookingId = bookingId,
                                alreadyPaid = false,
                            ),
                        )
                }
            } finally {
                _isPaying.value = false
            }
        }
    }

    override fun signContractAsRenter(bookingId: String) {
        if (bookingId in _actionInProgress.value) return

        coroutineScope.safeLaunchWithErrorHandler(
            isLoading = { false },
            setLoading = { },
            setError = { message ->
                coroutineScope.launch {
                    _signEffects.emit(
                        ContractSignEffect.Fail(
                            bookingId = bookingId,
                            message = message ?: "Не удалось подписать договор",
                        ),
                    )
                }
                _error.value = message
            },
            errorHandler = { e ->
                logRenterBookingActionError(
                    action = "signContractAsRenter",
                    bookingId = bookingId,
                    exception = e,
                )
                ErrorHandler.extractErrorMessage(
                    exception = e,
                    defaultNetworkError = "Ошибка сети",
                    defaultGenericError = "Не удалось подписать договор",
                )
            },
        ) {
            _actionInProgress.update { it + bookingId }
            try {
                val updated = booking.signContractAsRenter(bookingId)
                println(
                    "✅ [MyBookingsAsRenterViewModel] signContractAsRenter succeeded bookingId=$bookingId status=${updated.status}",
                )
                _error.value = null
                _bookings.update { list ->
                    list.map { if (it.id == bookingId) updated else it }
                }
                _signEffects.emit(ContractSignEffect.Ok(bookingId))
            } finally {
                _actionInProgress.update { it - bookingId }
            }
        }
    }
}

private fun logRenterBookingActionError(
    action: String,
    bookingId: String,
    exception: Throwable,
) {
    println("❌ [MyBookingsAsRenterViewModel] $action failed bookingId=$bookingId")
    println("   exception: ${exception::class.simpleName}")
    println("   message: ${exception.message}")
    if (exception is NetworkException) {
        println("   httpStatus: ${exception.statusCodeValue}")
    }
    exception.printStackTrace()
}
